import { Injectable, OnDestroy } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { z } from 'zod';
import { NotificationService } from '../notification-service/notification.service';
import { SavedListCompanyItem, SavedListType } from './saved-lists-models';
import {
  SavedItemCreate,
  SavedItemResponse,
  SavedItemsService,
  SavedItemUpdate,
} from 'ldt-state-storage-api';
import { AuthService } from '../../auth/service/auth.service';
import { StateStorageBaseService } from '../../core/services/state-storage-base.service';
import { Log } from 'src/app/logger.service';
import {
  StoredItemConfig,
  BaseStoredItem,
} from '../components/stored-items-browser/stored-items-browser.component';
import { SavedListItemRendererComponent } from '../components/stored-items-browser/saved-list-item-renderer.component';

const SavedListPayloadSchema = z.object({
  companies: z.array(z.any()), // Assuming items can be anything for now
  category: z.string().optional(),
  itemType: z.nativeEnum(SavedListType),
});

const SavedListSchema = z.object({
  itemType: z.nativeEnum(SavedListType),
  id: z.string(),
  name: z.string(),
  category: z.string().optional(),
  createdAt: z.string(),
  createdBy: z.string(),
  items: z.array(z.any()), // Assuming SavedListCompanyItem can be anything for now
  scope: z.string(),
});
export type SavedList = z.infer<typeof SavedListSchema>;

@Injectable({
  providedIn: 'root',
})
export class SavedListsService extends StateStorageBaseService<SavedList> implements OnDestroy {
  lists$ = this.items$;

  companyLists$ = this.items$.pipe(
    map((items) => {
      if (!items) return null;
      return items.filter((item) => item.itemType === SavedListType.Companies);
    })
  );

  constructor(
    stateStorageService: SavedItemsService,
    authService: AuthService,
    notificationService: NotificationService
  ) {
    super(stateStorageService, authService, notificationService);
  }

  getResourceType(): string {
    return 'value_list';
  }

  toApiModel(list: SavedList): SavedItemCreate | SavedItemUpdate {
    return {
      name: list.name,
      payload: {
        companies: list.items,
        category: list.category,
        itemType: list.itemType,
      },
      scope: 'org' as const,
      item_type_name: this.getResourceType(),
    };
  }

  fromApiModel(response: SavedItemResponse): SavedList | null {
    const rawPayload =
      typeof response.payload === 'string' ? JSON.parse(response.payload) : response.payload;

    try {
      const payload = SavedListPayloadSchema.parse(rawPayload);

      const savedList: SavedList = {
        id: response.id.toString(),
        name: response.name || 'Untitled List',
        category: payload.category,
        createdAt: response.created_at,
        createdBy: response.created_by.name || '',
        items: payload.companies || [],
        itemType: payload.itemType,
        scope: response.scope,
      };

      return savedList;
    } catch (error) {
      Log.s('Failed to parse saved list from API', 'error', {
        type: this.getResourceType(),
        id: response.id,
        name: response.name,
        payload: rawPayload,
        error: error instanceof z.ZodError ? error.toString() : String(error),
      });
      return null;
    }
  }

  protected afterCreate(item: SavedList, response: SavedItemResponse): SavedList | null {
    const created = super.afterCreate(item, response);
    if (created) {
      this.sortCachedItems();
    }
    return created;
  }

  protected afterUpdate(item: SavedList, response: SavedItemResponse): SavedList | null {
    const updated = super.afterUpdate(item, response);
    if (updated) {
      this.sortCachedItems();
    }
    return updated;
  }

  private sortCachedItems(): void {
    const items = this.getCachedItems();
    if (items) {
      const sorted = [...items].sort((a, b) => a.name.localeCompare(b.name));
      this.updateCache(sorted);
    }
  }

  public saveList(id: string | null, item: SavedList): Observable<SavedList> {
    return super.saveItem(id, item);
  }

  public deleteList(listId: string): void {
    this.delete(listId).subscribe();
  }

  getBrowserConfig(): StoredItemConfig<SavedList & BaseStoredItem> {
    return {
      title: 'Company Lists',
      itemTypeName: 'Lists',
      description: 'Select from our curated collection or your saved lists',
      searchPlaceholder: 'Search lists...',
      useCategories: true,
      // Using default tabs (Built-In Items / My Items based on scope)
      itemCountLabel: (count) => `${count} lists`,
      allowDelete: true,
      defaultComponentRenderer: SavedListItemRendererComponent,
      onDelete: (item) => this.deleteList(item.id),
    };
  }

  ngOnDestroy(): void {
    this.destroy();
  }
}
